import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, PanelRight } from 'lucide-react'
import type { Deal, FieldDefinition, Stage } from '@/api/types'
import { useMembers } from '@/features/team/useTeam'
import {
  COLUMNS,
  CUSTOM_COLUMN_PREFIX,
  PAGE_SIZES,
  customTableColumns,
  type ColumnDef,
  type ColumnId,
  type PageSize,
  type TableSettings,
} from '@/features/board/tableSettings'
import Select from '@/components/ui/Select'
import ColumnHeaderMenu, { type SortDir } from '@/components/ui/ColumnHeaderMenu'
import TableCard from '@/components/layout/TableCard'
import { cn } from '@/lib/cn'
import { formatCustomFieldValue } from '@/lib/customField'
import { formatDealValue, formatShortDate, isOverdue } from '@/lib/dealFormat'
import { pageItems } from '@/lib/pageItems'

interface SortState {
  column: ColumnId
  dir: SortDir
}

type SortKey = string | number | null

/** Compares two non-null sort keys of the same column (numbers or strings). */
function compareKeys(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  const sa = String(a)
  const sb = String(b)
  return sa < sb ? -1 : sa > sb ? 1 : 0
}

export default function DealTable({
  deals,
  stagesById,
  onOpen,
  onPreview,
  settings,
  onSettingsChange,
  currency,
  fieldDefs,
}: {
  /** Deals already in the default order (by stage position). */
  deals: Deal[]
  stagesById: Map<string, Stage>
  /** Row click: open the deal full-screen (same window). */
  onOpen: (id: string) => void
  /** Preview icon: open the deal in the side panel. */
  onPreview: (id: string) => void
  settings: TableSettings
  onSettingsChange: (next: TableSettings) => void
  /** Agency display currency, passed from Board (not fetched per row). */
  currency: string
  /** Deal custom-field definitions, for the 'custom.<key>' columns. */
  fieldDefs: FieldDefinition[]
}) {
  const [sort, setSort] = useState<SortState | null>(null)
  const [page, setPage] = useState(1)
  const { data: members } = useMembers()

  // Built-in + per-agency custom columns, and a fast id → def lookup.
  const allColumns = useMemo<ColumnDef[]>(
    () => [...COLUMNS, ...customTableColumns(fieldDefs)],
    [fieldDefs],
  )
  const columnsById = useMemo(
    () => new Map(allColumns.map((c) => [c.id, c])),
    [allColumns],
  )
  const fieldDefsByKey = useMemo(
    () => new Map(fieldDefs.map((d) => [d.fieldKey, d])),
    [fieldDefs],
  )

  // Visible columns in the user's chosen ORDER (dropping any whose def was removed).
  const visibleColumns = useMemo(
    () => settings.columns.map((id) => columnsById.get(id)).filter((c): c is ColumnDef => !!c),
    [settings.columns, columnsById],
  )

  // Sorting on a since-removed column falls back to the default stage order.
  const activeSort = sort && settings.columns.includes(sort.column) ? sort : null

  const memberNames = useMemo(
    () => new Map((members ?? []).map((m) => [m.id, m.fullName])),
    [members],
  )

  /** Stage board position by id — stagesById is built in board order. */
  const stageIndex = useMemo(() => {
    const map = new Map<string, number>()
    let i = 0
    for (const id of stagesById.keys()) map.set(id, i++)
    return map
  }, [stagesById])

  const sorted = useMemo(() => {
    if (!activeSort) return deals
    const { column, dir } = activeSort
    const sign = dir === 'asc' ? 1 : -1
    const keyOf = (deal: Deal): SortKey => {
      switch (column) {
        case 'name':
          return deal.title.toLowerCase()
        case 'stage':
          return stageIndex.get(deal.stageId) ?? null
        case 'nextContactDate':
          return deal.nextContactDate // ISO strings sort lexicographically
        case 'value':
          return deal.value
        case 'owner':
          return deal.ownerUserId
            ? (memberNames.get(deal.ownerUserId)?.toLowerCase() ?? null)
            : null
        case 'created':
          return deal.createdAt
        default: {
          if (!column.startsWith(CUSTOM_COLUMN_PREFIX)) return null
          const raw = deal.custom[column.slice(CUSTOM_COLUMN_PREFIX.length)]
          if (raw == null || raw === '') return null
          return typeof raw === 'number' ? raw : String(raw).toLowerCase()
        }
      }
    }
    // Decorate once so the key is computed O(n), not O(n log n) — matters at 2000+ deals.
    return deals
      .map((deal) => ({ deal, key: keyOf(deal) }))
      .sort((a, b) => {
        if (a.key === null) return b.key === null ? 0 : 1 // nulls last either direction
        if (b.key === null) return -1
        return sign * compareKeys(a.key, b.key)
      })
      .map((x) => x.deal)
  }, [deals, activeSort, stageIndex, memberNames])

  const total = sorted.length
  const pageCount = Math.max(1, Math.ceil(total / settings.pageSize))
  const safePage = Math.min(page, pageCount)
  const start = (safePage - 1) * settings.pageSize
  const pageDeals = useMemo(
    () => sorted.slice(start, start + settings.pageSize),
    [sorted, start, settings.pageSize],
  )

  /** Header click cycles asc → desc → back to default stage order. */
  function cycleSort(column: ColumnId) {
    setPage(1)
    setSort((s) =>
      s?.column !== column
        ? { column, dir: 'asc' }
        : s.dir === 'asc'
          ? { column, dir: 'desc' }
          : null,
    )
  }

  function sortBy(column: ColumnId, dir: SortDir) {
    setPage(1)
    setSort({ column, dir })
  }

  function removeColumn(column: ColumnId) {
    onSettingsChange({ ...settings, columns: settings.columns.filter((c) => c !== column) })
  }

  // Columns not currently shown (built-in + custom) — offered under "Add column"
  // in each header menu, mirroring HubSpot's column ⋮ menu.
  const hiddenColumns = useMemo(
    () => allColumns.filter((c) => !settings.columns.includes(c.id)).map((c) => ({ id: c.id, label: c.label })),
    [allColumns, settings.columns],
  )

  function addColumn(column: ColumnId) {
    if (settings.columns.includes(column)) return
    // Append so the added column lands at the end; the user can reorder it in table settings.
    onSettingsChange({ ...settings, columns: [...settings.columns, column] })
  }

  function changePageSize(size: PageSize) {
    setPage(1)
    onSettingsChange({ ...settings, pageSize: size })
  }

  const cellPad = settings.rowHeight === 'compact' ? 'px-4 py-1' : 'px-4 py-2.5'
  const rowHover = settings.zebra ? 'hover:bg-gray-100/60' : 'hover:bg-gray-50/50'

  function renderCell(deal: Deal, column: ColumnId) {
    switch (column) {
      case 'name':
        return (
          <td key={column} className={cn(cellPad, 'font-medium text-brand-dark')}>
            <span className="inline-flex items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation() // don't also trigger the row's full-screen open
                  onPreview(deal.id)
                }}
                title="Preview in side panel"
                aria-label="Preview"
                className="text-gray-300 hover:text-brand-primary opacity-0 group-hover:opacity-100"
              >
                <PanelRight size={14} />
              </button>
              {deal.title}
            </span>
          </td>
        )
      case 'photo':
        return (
          <td key={column} className={cn(cellPad, 'w-16')}>
            {deal.coverUrl && (
              <img src={deal.coverUrl} alt="" loading="lazy" className="w-14 h-10 rounded object-cover border border-gray-100" />
            )}
          </td>
        )
      case 'stage': {
        const stage = stagesById.get(deal.stageId)
        return (
          <td key={column} className={cn(cellPad, 'text-gray-600 whitespace-nowrap')}>
            {stage ? (
              <span className="inline-flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: stage.color ?? '#94a3b8' }}
                />
                {stage.name}
              </span>
            ) : (
              '—'
            )}
          </td>
        )
      }
      case 'nextContactDate':
        return (
          <td
            key={column}
            className={cn(
              cellPad,
              'whitespace-nowrap',
              deal.nextContactDate && isOverdue(deal.nextContactDate)
                ? 'text-red-600'
                : 'text-gray-600',
            )}
          >
            {deal.nextContactDate ? formatShortDate(deal.nextContactDate) : '—'}
          </td>
        )
      case 'value':
        return (
          <td key={column} className={cn(cellPad, 'text-gray-600 whitespace-nowrap')}>
            {deal.value != null ? formatDealValue(deal.value, currency) : '—'}
          </td>
        )
      case 'owner':
        return (
          <td key={column} className={cn(cellPad, 'text-gray-600 whitespace-nowrap')}>
            {(deal.ownerUserId && memberNames.get(deal.ownerUserId)) || '—'}
          </td>
        )
      case 'created':
        return (
          <td key={column} className={cn(cellPad, 'text-gray-600 whitespace-nowrap')}>
            {formatShortDate(deal.createdAt)}
          </td>
        )
      default: {
        // custom.<key> column — value from the deal's custom map, formatted per its definition
        const def = column.startsWith(CUSTOM_COLUMN_PREFIX)
          ? fieldDefsByKey.get(column.slice(CUSTOM_COLUMN_PREFIX.length))
          : undefined
        const text = def ? formatCustomFieldValue(def, deal.custom[def.fieldKey]) : null
        return (
          <td key={column} className={cn(cellPad, 'text-gray-600 whitespace-nowrap')}>
            {text ?? '—'}
          </td>
        )
      }
    }
  }

  return (
    <TableCard
      fillHeight
      footer={
        total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2.5">
            <span className="text-xs text-gray-500">
              Showing {(start + 1).toLocaleString('en-AU')}–
              {(start + pageDeals.length).toLocaleString('en-AU')} of {total.toLocaleString('en-AU')}
            </span>
            <div className="flex items-center gap-3">
              <nav className="flex items-center gap-0.5" aria-label="Pagination">
                <button
                  type="button"
                  onClick={() => setPage(safePage - 1)}
                  disabled={safePage <= 1}
                  aria-label="Previous page"
                  className="p-1 rounded text-gray-400 hover:text-brand-dark disabled:opacity-40 disabled:hover:text-gray-400"
                >
                  <ChevronLeft size={15} />
                </button>
                {pageItems(safePage, pageCount).map((item, i) =>
                  item === '…' ? (
                    <span key={`gap-${i}`} className="px-1 text-xs text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPage(item)}
                      aria-current={item === safePage ? 'page' : undefined}
                      className={cn(
                        'min-w-6 px-1.5 py-1 rounded text-xs',
                        item === safePage
                          ? 'bg-brand-primary/10 text-brand-dark font-medium'
                          : 'text-gray-500 hover:text-brand-dark',
                      )}
                    >
                      {item.toLocaleString('en-AU')}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  onClick={() => setPage(safePage + 1)}
                  disabled={safePage >= pageCount}
                  aria-label="Next page"
                  className="p-1 rounded text-gray-400 hover:text-brand-dark disabled:opacity-40 disabled:hover:text-gray-400"
                >
                  <ChevronRight size={15} />
                </button>
              </nav>
              <Select
                size="xs"
                value={settings.pageSize}
                onChange={(e) => changePageSize(Number(e.target.value) as PageSize)}
                aria-label="Rows per page"
                className="w-28"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )
      }
    >
      <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-gray-500 text-left text-xs uppercase">
            <tr>
              {visibleColumns.map((col, i) => (
                <th
                  key={col.id}
                  className="px-4 py-2 group"
                  aria-sort={
                    activeSort?.column === col.id
                      ? activeSort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => cycleSort(col.id)}
                      className="flex items-center gap-1 uppercase hover:text-brand-dark"
                      title="Sort"
                    >
                      {col.label}
                      {activeSort?.column === col.id &&
                        (activeSort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                    </button>
                    <ColumnHeaderMenu
                      canRemove={!col.locked}
                      onSort={(dir) => sortBy(col.id, dir)}
                      onRemove={() => removeColumn(col.id)}
                      hiddenColumns={hiddenColumns}
                      onAdd={addColumn}
                      align={i === 0 ? 'left' : 'right'}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {total === 0 && (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  className="px-4 py-6 text-center text-gray-400"
                >
                  No deals in this pipeline yet.
                </td>
              </tr>
            )}
            {pageDeals.map((deal, i) => (
              <tr
                key={deal.id}
                onClick={() => onOpen(deal.id)}
                className={cn(
                  'group border-t border-gray-100 cursor-pointer',
                  settings.zebra && i % 2 === 1 && 'bg-gray-50',
                  rowHover,
                )}
              >
                {visibleColumns.map((col) => renderCell(deal, col.id))}
              </tr>
            ))}
          </tbody>
      </table>
    </TableCard>
  )
}
